import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from 'react-hot-toast';
import PropTypes from 'prop-types';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const AddEditBatchModal = ({ open, onOpenChange, batch = null, onSave }) => {
    // Local state to manage form fields
    const [batchId, setBatchId] = useState('');
    const [capacity, setCapacity] = useState('50'); // Use string for input
    const [courseIdValue, setCourseIdValue] = useState('');
    const [courseUrl, setCourseUrl] = useState('');
    const [subscriptionId, setSubscriptionId] = useState(''); // Add subscription ID state
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [batchNamingFormat, setBatchNamingFormat] = useState('alphabetical'); // "alphabetical" or "numerical"

    const isEditing = !!batch; // Determine if we are editing or adding

    // Get batch prefix - either from batch PK or from the first part of the SK
    const getBatchPrefix = () => {
        if (batch) {
            // If editing, extract prefix from the SK (format is typically "Prefix-Suffix")
            if (batch.SK && batch.SK.includes('-')) {
                return batch.SK.split('-')[0];
            }
            // Otherwise use PK (course name)
            return batch.PK || "Batch";
        }
        return "Batch";
    };

    // Handle CourseId change - auto-generate CourseUrl if needed
    const handleCourseIdChange = (e) => {
        const newCourseId = e.target.value.trim();
        setCourseIdValue(newCourseId);
        
        // If courseUrl is empty and courseId is not, auto-generate URL
        if (newCourseId && !courseUrl) {
            setCourseUrl(`https://classplusapp.com/diy/courses/course-overview?id=${newCourseId}`);
        }
    };

    useEffect(() => {
        if (open) {
            if (isEditing && batch) {
                // Populate fields if editing
                setBatchId(batch.SK || ''); // Batch ID (SK) is fixed when editing
                setCourseIdValue(batch.courseId || '');
                setCourseUrl(batch.courseUrl || '');
                setCapacity(String(batch.capacity || 50));
                setSubscriptionId(batch.subscriptionId || ''); // Set subscription ID if available
                
                // Determine batch naming format from existing batch ID
                const hasNumericSuffix = /^[\w-]+-\d+$/.test(batch.SK || '');
                setBatchNamingFormat(hasNumericSuffix ? 'numerical' : 'alphabetical');
                
                setErrors({}); // Clear errors when modal opens or data changes
            } else {
                // Reset fields if adding or if batch becomes null
                setBatchId(''); 
                setCourseIdValue('');
                setCourseUrl('');
                setCapacity('50');
                setSubscriptionId(''); // Reset subscription ID field
                setBatchNamingFormat('alphabetical'); // Default to alphabetical for new batches
                setErrors({});
            }

            // Focus the first input when modal opens
            setTimeout(() => {
                const firstInput = document.querySelector('#batchId');
                if (firstInput && !isEditing) {
                    firstInput.focus();
                } else {
                    const courseIdInput = document.querySelector('#courseId');
                    if (courseIdInput) {
                        courseIdInput.focus();
                    }
                }
            }, 100);
        } else {
            // Cleanup when modal closes
            setIsLoading(false);
            setErrors({});
        }
    }, [open, batch, isEditing]); // Rerun effect when modal opens or batch changes

    // ✅ FIXED: Add cleanup on unmount
    useEffect(() => {
        return () => {
            // Cleanup function
            setIsLoading(false);
            setErrors({});
        };
    }, []);

    const validateInput = () => {
        const newErrors = {};
        if (!isEditing && !batchId.trim()) {
             newErrors.batchId = 'Batch ID is required when creating a new batch.';
        }
        
        // Validate batch ID format: should be CourseName-Letter (e.g., Course-A, Physics-AB)
        if (!isEditing && batchId.trim()) {
            const batchIdRegex = /^[\w-]+-[A-Z]+$/;
            if (!batchIdRegex.test(batchId.trim())) {
                newErrors.batchId = 'Batch ID should be in format "CourseName-A" (course name followed by hyphen and alphabetical suffix).';
            }
        }
        
        // Validate batch ID format based on selected naming format
        if (!isEditing && batchId.trim()) {
            if (batchNamingFormat === 'alphabetical') {
                // For alphabetical: CourseName-A, CourseName-B, CourseName-AA, etc.
                const batchIdRegex = /^[\w-]+-[A-Z]+$/;
                if (!batchIdRegex.test(batchId.trim())) {
                    newErrors.batchId = 'Batch ID should be in format "CourseName-A" (course name followed by hyphen and alphabetical suffix).';
                }
            } else {
                // For numerical: CourseName-1, CourseName-2, etc.
                const batchIdRegex = /^[\w-]+-\d+$/;
                if (!batchIdRegex.test(batchId.trim())) {
                    newErrors.batchId = 'Batch ID should be in format "CourseName-1" (course name followed by hyphen and numerical suffix).';
                }
            }
        }
        
        const capacityNum = parseInt(capacity);
        if (isNaN(capacityNum) || capacityNum <= 0) {
             newErrors.capacity = 'Capacity must be a positive number.';
        }
        // Check if courseId exists but courseUrl is missing
        if (courseIdValue && !courseUrl) {
             newErrors.courseUrl = 'Course URL should be provided when Course ID is set.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateInput()) {
            return;
        }
        setIsLoading(true);

        try {
            const batchData = {
                SK: batchId,
                capacity: parseInt(capacity),
                count: batch?.count || 0,
                courseId: courseIdValue,
                courseUrl: courseUrl,
                subscriptionId: subscriptionId // Include subscription ID in the data
            };
            
            await onSave(batchData);
            toast.success(`Batch ${isEditing ? 'updated' : 'created'} successfully.`);
            onOpenChange(false); // Close the modal
        } catch (error) {
            console.error(`Error ${isEditing ? 'updating' : 'creating'} batch:`, error);
            const errorMsg = error.message || `Failed to ${isEditing ? 'update' : 'create'} batch.`;
            setErrors({ form: errorMsg });
            toast.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isEditing ? `Edit Batch: ${batch?.SK}` : 'Add New Batch'}</DialogTitle>
                    <DialogDescription>
                         {isEditing ? "Update the batch details below." : "Enter details for the new batch."}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        {/* Batch ID field - only editable when adding */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="batchId" className="text-right">Batch ID</Label>
                            <div className="col-span-3 space-y-1">
                                <Input 
                                    id="batchId"
                                    value={batchId}
                                    onChange={(e) => setBatchId(e.target.value)} 
                                    disabled={isEditing} // Disable if editing
                                    className={`w-full ${errors.batchId ? 'border-red-500' : ''}`}
                                    required={!isEditing}
                                    placeholder={`${getBatchPrefix()}-${batchNamingFormat === 'alphabetical' ? 'A' : '1'}`}
                                />
                                {!isEditing && (
                                    <p className="text-xs text-muted-foreground">
                                        {batchNamingFormat === 'alphabetical'
                                            ? 'Format: CourseName-A, CourseName-B, etc. Use alphabetical suffixes.'
                                            : 'Format: CourseName-1, CourseName-2, etc. Use numerical suffixes.'}
                                    </p>
                                )}
                                {errors.batchId && <p className="text-red-500 text-sm">{errors.batchId}</p>}
                            </div>
                        </div>

                        {/* Batch naming format - only visible when adding */}
                        {!isEditing && (
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="batchNamingFormat" className="text-right">Naming Format</Label>
                                <div className="col-span-3 space-y-1">
                                    <Select 
                                        value={batchNamingFormat} 
                                        onValueChange={(value) => setBatchNamingFormat(value)}
                                        disabled={isEditing}
                                    >
                                        <SelectTrigger id="batchNamingFormat">
                                            <SelectValue placeholder="Select batch naming format" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="alphabetical">Alphabetical (Course-A, Course-B...)</SelectItem>
                                            <SelectItem value="numerical">Numerical (Course-1, Course-2...)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">
                                        {batchNamingFormat === 'alphabetical' 
                                            ? 'Format: CourseName-A, CourseName-B, etc.' 
                                            : 'Format: CourseName-1, CourseName-2, etc.'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Course ID field */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="courseId" className="text-right">Course ID</Label>
                            <Input 
                                id="courseId"
                                value={courseIdValue} 
                                onChange={handleCourseIdChange}
                                className={`col-span-3 ${errors.courseId ? 'border-red-500' : ''}`}
                                placeholder="Enter the Classplus course ID"
                            />
                            {errors.courseId && <p className="col-span-4 text-red-500 text-sm text-right">{errors.courseId}</p>}
                        </div>

                        {/* Course URL field */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="courseUrl" className="text-right">Course URL</Label>
                            <Input 
                                id="courseUrl"
                                value={courseUrl} 
                                onChange={(e) => setCourseUrl(e.target.value)}
                                className={`col-span-3 ${errors.courseUrl ? 'border-red-500' : ''}`}
                                placeholder="The URL to the course on Classplus"
                            />
                            {errors.courseUrl && <p className="col-span-4 text-red-500 text-sm text-right">{errors.courseUrl}</p>}
                        </div>

                        {/* Capacity field */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="capacity" className="text-right">Capacity</Label>
                            <Input 
                                id="capacity"
                                type="number"
                                value={capacity} 
                                onChange={(e) => setCapacity(e.target.value)} 
                                min="1" 
                                className={`col-span-3 ${errors.capacity ? 'border-red-500' : ''}`}
                                required
                            />
                            {errors.capacity && <p className="col-span-4 text-red-500 text-sm text-right">{errors.capacity}</p>}
                        </div>
                        
                        {/* Subscription ID field */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="subscriptionId" className="text-right">Subscription ID</Label>
                            <div className="col-span-3 space-y-1">
                                <Input 
                                    id="subscriptionId"
                                    value={subscriptionId} 
                                    onChange={(e) => setSubscriptionId(e.target.value)}
                                    className="w-full"
                                    placeholder="Pabbly subscription list ID (optional)"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Enter Pabbly subscription list ID for this batch. If left blank, one will be created automatically if Pabbly API is configured.
                                </p>
                            </div>
                        </div>
                        
                        {errors.form && (
                            <p className="text-red-500 text-sm text-center col-span-4">{errors.form}</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? "Saving..." : (isEditing ? 'Update Batch' : 'Add Batch')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// PropTypes validation
AddEditBatchModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onOpenChange: PropTypes.func.isRequired,
    batch: PropTypes.object,
    onSave: PropTypes.func.isRequired
};

export default AddEditBatchModal; 